import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  FormControlLabel,
  Switch,
  Alert,
  Checkbox,
  ListItemText,
  Chip,
  Divider,
  Card,
  CardContent,
  Autocomplete
} from '@mui/material';
import LocalizedDatePicker from '../common/LocalizedDatePicker';
import { styled, useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { format, isValid } from 'date-fns';
import { useAuth } from '../../context/AuthContext';
import useFetchObjects from '../../api/useFetchObjects';
import useAdd from '../../api/useAdd';
import axios from 'axios';
import { RTLTextField, RTLFormControl, RTLAutocomplete, RTLDialogTitle, RTLDialogContent, RTLDialogActions } from '../../styles/rtlComponents';
import useRTL from '../../hooks/useRTL';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

// Using centralized RTL components from styles/rtlComponents.js

const BulkAssignForm = ({ open, onClose, onSuccess }) => {
  const { t, i18n } = useTranslation(['tasks', 'common', 'documents']);
  const { user } = useAuth();
  const token = user?.token;
  
  // Form state
  const [assignmentType, setAssignmentType] = useState('users');
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [departmentId, setDepartmentId] = useState('');
  const [documentId, setDocumentId] = useState('');
  const [dueDate, setDueDate] = useState(null);
  const [notes, setNotes] = useState('');
  const [informationalOnly, setInformationalOnly] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // API hooks with error handling
  const { 
    data: usersData, 
    isLoading: usersLoading,
    error: usersError
  } = useFetchObjects('users', { limit: 1000, page: 1 }, token);
  
  const { 
    data: departmentsData, 
    isLoading: departmentsLoading,
    error: departmentsError
  } = useFetchObjects('departments', { limit: 1000, page: 1 }, token);
  
  const { 
    data: documentsData, 
    isLoading: documentsLoading,
    error: documentsError,
    refetch: refetchDocuments
  } = useFetchObjects('documents', { limit: 100 }, token);

  const {
    data: taskAssignmentsData = { data: [] },
    isLoading: taskAssignmentsLoading,
    error: taskAssignmentsError,
    refetch: refetchAssignments
  } = useFetchObjects('task-assignments', { limit: 1000 }, token);
  
  // Display API fetch errors
  useEffect(() => {
    const errors = [];
    if (usersError) errors.push(`Error loading users: ${usersError.message}`);
    if (departmentsError) errors.push(`Error loading departments: ${departmentsError.message}`);
    if (documentsError) errors.push(`Error loading documents: ${documentsError.message}`);
    if (taskAssignmentsError) errors.push(`Error loading task assignments: ${taskAssignmentsError.message}`);
    
    if (errors.length > 0) {
      setError(errors.join('. '));
    }
  }, [usersError, departmentsError, documentsError, taskAssignmentsError]);
  
  // Refetch data when form opens
  useEffect(() => {
    if (open) {
      refetchDocuments();
      refetchAssignments();
    }
  }, [open, refetchDocuments, refetchAssignments]);
  
  // Helper functions to handle different API response structures
  const getUsers = () => {
    if (!usersData) return [];
    if (usersData.users && Array.isArray(usersData.users)) return usersData.users;
    if (usersData.data && Array.isArray(usersData.data)) return usersData.data;
    if (Array.isArray(usersData)) return usersData;
    return [];
  };
  
  const getDepartments = () => {
    if (!departmentsData) return [];
    if (departmentsData.departments && Array.isArray(departmentsData.departments)) return departmentsData.departments;
    if (departmentsData.data && Array.isArray(departmentsData.data)) return departmentsData.data;
    if (Array.isArray(departmentsData)) return departmentsData;
    return [];
  };
  
  const getDocuments = () => {
    if (!documentsData) return [];
    if (documentsData.documents && Array.isArray(documentsData.documents)) return documentsData.documents;
    if (documentsData.data && Array.isArray(documentsData.data)) return documentsData.data;
    if (Array.isArray(documentsData)) return documentsData;
    return [];
  };

  const getTaskAssignments = () => {
    if (!taskAssignmentsData) return [];
    if (taskAssignmentsData.assignments && Array.isArray(taskAssignmentsData.assignments)) return taskAssignmentsData.assignments;
    if (taskAssignmentsData.data && Array.isArray(taskAssignmentsData.data)) return taskAssignmentsData.data;
    if (Array.isArray(taskAssignmentsData)) return taskAssignmentsData;
    return [];
  };

  const getAvailableDocuments = () => {
    const allDocuments = getDocuments();
    const assignments = getTaskAssignments();
    const assignedDocIds = new Set();
    assignments.forEach(a => {
      if (a.document_id) assignedDocIds.add(a.document_id);
    });
    return allDocuments.filter(doc => !assignedDocIds.has(doc.id));
  };
  
  // Reset form when opened
  useEffect(() => {
    if (open) {
      setAssignmentType('users');
      setSelectedUserIds([]);
      setDepartmentId('');
      setDocumentId('');
      setDueDate(null);
      setNotes('');
      setInformationalOnly(false);
      setError(null);
      setLoading(false);
    }
  }, [open]);
  
  const handleSubmit = async () => {
    try {
      setError(null);
      setLoading(true);
      
      // Form validation
      if (assignmentType === 'users' && selectedUserIds.length === 0) {
        setError(t('tasks:error_select_users', 'Please select at least one user'));
        setLoading(false);
        return;
      }
      
      if (assignmentType === 'department' && !departmentId) {
        setError(t('tasks:error_select_department', 'Please select a department'));
        setLoading(false);
        return;
      }
      
      if (!documentId) {
        setError(t('documents:error_select_documents', 'Please select a document'));
        setLoading(false);
        return;
      }
      
      // Prepare formatted due date
      let formattedDueDate = undefined;
      if (dueDate && isValid(new Date(dueDate))) {
        try {
          formattedDueDate = format(new Date(dueDate), 'yyyy-MM-dd');
        } catch (fmtErr) {
          console.warn('Invalid due date, skipping due_date field');
        }
      }

      // Prepare assignment data
      const assignmentData = {
        documentIds: [documentId],
        notes,
        ...(formattedDueDate ? { due_date: formattedDueDate } : {}),
        metadata: {
          assigned_by: user.id,
          assigned_by_name: user.full_name || user.username,
          bulk_assignment: true,
          informational_only: informationalOnly
        }
      };
      
      // Add assignees based on type
      if (assignmentType === 'users') {
        assignmentData.assigneeIds = selectedUserIds;
      } else {
        assignmentData.department_id = departmentId;
      }

      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/task-assignments/bulk-assign`,
        assignmentData,
        { headers: { Authorization: `Bearer ${user.token}` }}
      );
      
      if (response.status >= 200 && response.status < 300) {
        setLoading(false);
        onSuccess(response.data);
        onClose();
      } else {
        throw new Error(response.data?.message || 'Failed to create assignments');
      }
    } catch (err) {
      console.error('Error creating assignments:', err);
      setError(err.response?.data?.message || t('tasks:assignment_error', 'Failed to create assignments'));
      setLoading(false);
    }
  };
  
  const { isRTL, getRTLProps } = useRTL();
  const availableDocuments = getAvailableDocuments();
  
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth dir={isRTL ? 'rtl' : 'ltr'}>
      <RTLDialogTitle {...getRTLProps('dialogtitle')}>
        {t('tasks:bulk_assign_tasks', 'Bulk Assign Tasks')}
      </RTLDialogTitle>
      
      <RTLDialogContent {...getRTLProps('dialogcontent')}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        {/* Form Description */}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('tasks:bulk_assign_description', 'Assign multiple tasks to users or an entire department at once')}
        </Typography>
        
       
        
        {/* Assignee Selection */}
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t('tasks:assignee_selection', 'Assignee Selection')}
            </Typography>
            
            <Grid container spacing={2}>
              {assignmentType === 'users' ? (
                <Grid item xs={12}>
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ textAlign: isRTL ? 'right' : 'left' }}>
                      {usersLoading 
                        ? t('common:loading_users', 'Loading users...') 
                        : t('tasks:users_available', '{{count}} users available', { count: getUsers().length })
                      }
                    </Typography>
                  </Box>
                  <RTLAutocomplete
                    fullWidth
                    {...getRTLProps('autocomplete')}
                    multiple
                    options={getUsers()}
                    getOptionLabel={(option) => option.full_name || option.username || t('common:unnamed_user', 'Unnamed user')}
                    value={getUsers().filter(user => selectedUserIds.includes(user.id))}
                    onChange={(event, newValue) => setSelectedUserIds(newValue.map(user => user.id))}
                    loading={usersLoading}
                    disabled={loading}
                    filterOptions={(options, { inputValue }) => {
                      const searchTerm = inputValue.toLowerCase();
                      return options.filter(option => {
                        const fullName = (option.full_name || '').toLowerCase();
                        const username = (option.username || '').toLowerCase();
                        const email = (option.email || '').toLowerCase();
                        const department = (option.department?.name || '').toLowerCase();
                        
                        return fullName.includes(searchTerm) || 
                               username.includes(searchTerm) || 
                               email.includes(searchTerm) ||
                               department.includes(searchTerm);
                      });
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('tasks:select_users', 'Select Users')}
                        placeholder={t('tasks:search_users', 'Search by name, username, email, or department...')}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {usersLoading ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'primary.main',
                            },
                          },
                        }}
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          key={option.id}
                          label={option.full_name || option.username}
                          size="small"
                          sx={{
                            backgroundColor: 'primary.50',
                            color: 'primary.main',
                            border: '1px solid',
                            borderColor: 'primary.200',
                            '& .MuiChip-deleteIcon': {
                              display: 'none !important',
                            },
                          }}
                          {...getTagProps({ index })}
                        />
                      ))
                    }
                    renderOption={(props, option) => (
                      <Box component="li" {...props} sx={{ p: 2, borderBottom: '1px solid #f0f0f0', '&:hover': { bgcolor: '#f8f9fa' } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                          <Box 
                            sx={{ 
                              width: 32, 
                              height: 32, 
                              borderRadius: '50%', 
                              bgcolor: 'primary.main', 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center', 
                              color: 'white',
                              fontWeight: 'bold',
                              fontSize: '0.875rem',
                              flexShrink: 0
                            }}
                          >
                            {(option.full_name || option.username || 'U').charAt(0).toUpperCase()}
                          </Box>
                          <Box sx={{ flexGrow: 1, textAlign: isRTL ? 'right' : 'left', minWidth: 0 }}>
                            <Typography variant="body1" fontWeight="600" sx={{ mb: 0.5 }} noWrap>
                              {option.full_name || option.username || t('common:unnamed_user', 'Unnamed user')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" noWrap>
                              {option.email}
                            </Typography>
                            {option.department?.name && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} noWrap>
                                {option.department.name}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    )}
                    noOptionsText={t('tasks:no_users_found', 'No users found')}
                    loadingText={t('common:loading_users', 'Loading users...')}
                    sx={{
                      '& .MuiAutocomplete-paper': {
                        borderRadius: 2,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        border: '1px solid',
                        borderColor: 'divider',
                      },
                    }}
                  />
                </Grid>
              ) : (
                <Grid item xs={12}>
                  <RTLAutocomplete
                    fullWidth
                    {...getRTLProps('autocomplete')}
                    options={getDepartments()}
                    getOptionLabel={(option) => option.name || t('common:unnamed_department', 'Unnamed department')}
                    value={getDepartments().find(dept => dept.id === departmentId) || null}
                    onChange={(event, newValue) => setDepartmentId(newValue?.id || '')}
                    loading={departmentsLoading}
                    disabled={loading}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('tasks:select_department', 'Select Department')}
                        placeholder={t('tasks:search_department', 'Search for a department...')}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {departmentsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    noOptionsText={t('tasks:no_departments_found', 'No departments found')}
                    loadingText={t('common:loading_departments', 'Loading departments...')}
                  />
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
        
        {/* Document Selection */}
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t('documents:document_selection', 'Document Selection')}
            </Typography>
            
            {availableDocuments.length === 0 && getDocuments().length > 0 ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                {t('tasks:all_documents_assigned', 'All documents are already assigned to tasks. Create a new document to assign.')}
              </Alert>
            ) : null}
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <RTLAutocomplete
                  fullWidth
                  {...getRTLProps('autocomplete')}
                  options={availableDocuments}
                  getOptionLabel={(option) => `${option.title}${option.reference_number ? ` (${option.reference_number})` : ''}`}
                  value={availableDocuments.find(doc => doc.id === documentId) || null}
                  onChange={(event, newValue) => setDocumentId(newValue?.id || '')}
                  loading={documentsLoading || taskAssignmentsLoading}
                  disabled={loading || availableDocuments.length === 0}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('documents:select_document', 'Select Document')}
                      placeholder={t('documents:search_document', 'Search for a document...')}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {(documentsLoading || taskAssignmentsLoading) ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Box>
                        <Typography variant="body2">
                          {option.title}
                        </Typography>
                        {option.reference_number && (
                          <Typography variant="caption" color="text.secondary">
                            {t('documents:reference', 'Ref')}: {option.reference_number}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )}
                  noOptionsText={t('documents:no_documents_found', 'No documents found')}
                  loadingText={t('common:loading_documents', 'Loading documents...')}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
        
        {/* Task Details */}
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t('tasks:task_details', 'Task Details')}
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <LocalizedDatePicker
                  label={t('common:due_date', 'Due Date')}
                  value={dueDate}
                  onChange={(e) => setDueDate(e)}
                  name="dueDate"
                  isRtl={isRTL}
                  inputProps={{
                    disabled: loading,
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={informationalOnly} 
                      onChange={(e) => setInformationalOnly(e.target.checked)} 
                    />
                  }
                  label={t('tasks:informational_only', 'Informational Only')}
                />
                <Typography variant="caption" color="text.secondary" display="block">
                  {t('tasks:informational_only_desc', 'Tasks will be marked as completed automatically')}
                </Typography>
              </Grid>
              
              <Grid item xs={12}>
                <RTLTextField
                  label={t('tasks:notes', 'Notes')}
                  multiline
                  rows={3}
                  fullWidth
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  disabled={loading}
                  placeholder={t('tasks:notes_placeholder', 'Add any relevant instructions or context for this task')}
                  {...getRTLProps('textfield')}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </RTLDialogContent>
      
      <RTLDialogActions {...getRTLProps('dialogactions')}>
        <Button onClick={onClose} disabled={loading}>
          {t('common:cancel', 'Cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={loading || availableDocuments.length === 0}
          startIcon={loading && <CircularProgress size={20} />}
        >
          {loading 
            ? t('common:processing', 'Processing...') 
            : t('tasks:bulk_assign', 'Bulk Assign')}
        </Button>
      </RTLDialogActions>
    </Dialog>
  );
};

export default BulkAssignForm; 